import { useState } from 'react'
import { Mail, Phone, MessageCircle, Clock, Send, CheckCircle } from 'lucide-react'
import { platformIcons, platformNames } from '../data/config'
import { getTheme } from '../utils/colors'

function Contact({ config, isLight = false }) {
  const theme = getTheme(isLight, config.profile?.fontColor)
  const contact = config.contact
  const social = config.social
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    type: '产品测评',
    message: ''
  })
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    // 模拟提交
    setIsSubmitted(true)
    setTimeout(() => setIsSubmitted(false), 3000)
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const cooperationTypes = ['产品测评', '探店打卡', '品牌植入', '直播带货', '活动出席', '其他合作']

  return (
    <section id="contact" className="py-20" style={{ backgroundColor: config.profile?.sectionBackground || '#000000' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 标题 */}
        <div className="text-center mb-12">
          <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${theme.text}`}>
            商务合作
          </h2>
          <p className={`max-w-2xl mx-auto ${theme.textSecondary}`}>
            欢迎品牌方咨询合作，24小时内回复，期待与您共创优质内容
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* 左侧：联系信息 */}
          <div>
            <div className={`rounded-2xl p-8 border ${theme.cardBg} ${theme.cardBorder}`}>
              <h3 className={`text-xl font-semibold mb-6 ${theme.text}`}>联系方式</h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h4 className={`font-medium ${theme.text}`}>商务邮箱</h4>
                    <a href={`mailto:${contact.email}`} className={`${theme.textSecondary} hover:text-accent transition-colors`}>
                      {contact.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h4 className={`font-medium ${theme.text}`}>联系电话</h4>
                    <a href={`tel:${contact.phone}`} className={`${theme.textSecondary} hover:text-purple-400 transition-colors`}>
                      {contact.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h4 className={`font-medium ${theme.text}`}>微信</h4>
                    <p className={theme.textSecondary}>{contact.wechat}</p>
                    {contact.wechatQR && (
                      <div className="mt-3 p-2 bg-white rounded-lg inline-block">
                        <img src={contact.wechatQR} alt="微信二维码" className="w-32 h-32" />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h4 className={`font-medium ${theme.text}`}>工作时间</h4>
                    <p className={theme.textSecondary}>{contact.workHours || '工作日 10:00-18:00'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 社交平台 */}
            <div className={`mt-6 rounded-2xl p-8 border ${theme.cardBg} ${theme.cardBorder}`}>
              <h3 className={`text-xl font-semibold mb-4 ${theme.text}`}>关注我的平台</h3>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(social).map(([platform, url]) => {
                  if (platform === 'wechat') return null
                  return (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex flex-col items-center gap-2 p-3 rounded-lg transition-colors ${theme.chipBg} hover:bg-gray-700`}
                    >
                      <span className="text-2xl">{platformIcons[platform]}</span>
                      <span className={`text-xs ${theme.textSecondary}`}>{platformNames[platform]}</span>
                    </a>
                  )
                })}
              </div>
            </div>
          </div>

          {/* 右侧：联系表单 */}
          <div>
            <div className={`rounded-2xl p-8 border ${theme.cardBg} ${theme.cardBorder}`}>
              <h3 className={`text-xl font-semibold mb-6 ${theme.text}`}>发送合作咨询</h3>

              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                  <h4 className={`text-xl font-semibold mb-2 ${theme.text}`}>提交成功！</h4>
                  <p className={theme.textSecondary}>我们会在24小时内与您联系</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>
                        您的姓名 *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className={`w-full px-4 py-3 border rounded-lg placeholder-gray-500 focus:border-accent focus:outline-none ${theme.inputBg} ${theme.inputBorder} ${theme.text}`}
                        placeholder="请输入姓名"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>
                        公司名称
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg placeholder-gray-500 focus:border-accent focus:outline-none ${theme.inputBg} ${theme.inputBorder} ${theme.text}`}
                        placeholder="请输入公司名称"
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>
                      联系邮箱 *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 border rounded-lg placeholder-gray-500 focus:border-accent focus:outline-none ${theme.inputBg} ${theme.inputBorder} ${theme.text}`}
                      placeholder="请输入邮箱地址"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>
                      合作类型
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:border-accent focus:outline-none ${theme.inputBg} ${theme.inputBorder} ${theme.text}`}
                    >
                      {cooperationTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>
                      合作详情 *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className={`w-full px-4 py-3 border rounded-lg placeholder-gray-500 focus:border-accent focus:outline-none resize-none ${theme.inputBg} ${theme.inputBorder} ${theme.text}`}
                      placeholder="请简要描述您的合作需求，包括品牌、产品、预算范围等"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 bg-gradient-accent text-white font-semibold rounded-lg hover:bg-gradient-accent transition-all flex items-center justify-center gap-2"
                  >
                    <Send className="w-5 h-5" />
                    发送咨询
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Contact
